import { Blac } from "blac-next";
import type { TourDefinition, TourStepDefinition } from "@9amhealth/shared";
import ConversationListCubit from "src/ui/components/LeapChat/state/ConversationListCubit";
import { ChatTourAnchor } from "../chatTourConstants";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { chatConversationPath, CHAT_NEW_PATH } from "../leapChatPaths";

export const CHAT_TOUR_ID = "chat-tour-v1";

export enum ChatTourVariant {
  OldAndNewFromIssue = "oldAndNewFromIssue",
  OnlyOldMessages = "onlyOldMessages"
}

export interface ChatTourConfig {
  variant: ChatTourVariant;
  navigate: (path: string) => void;
  conversationHistoryId: string;
}

function resolveVariantOldAndNewFromIssueMessagesSteps(
  navigate: (path: string) => void,
  conversationHistoryId: string
): TourStepDefinition[] {
  const listCubit = Blac.getBloc(ConversationListCubit);

  return [
    {
      anchorName: ChatTourAnchor.DRAWER_TOGGLE,
      textKey: "tourGuide.chat.findPrevious",
      placement: "bottom"
    },
    {
      anchorName: ChatTourAnchor.HISTORY_ITEM,
      textKey: "tourGuide.chat.openOldThread",
      placement: "bottom",
      runBefore: () => listCubit.openDrawer()
    },
    {
      anchorName: ChatTourAnchor.NEW_CONVERSATION,
      textKey: "tourGuide.chat.startNewConversation",
      placement: "bottom",
      runBefore: () => {
        listCubit.closeDrawer();
        navigate(chatConversationPath(conversationHistoryId));
      }
    },
    {
      anchorName: ChatTourAnchor.COMPOSER,
      textKey: "tourGuide.chat.askSophie",
      placement: "top",
      buttonTextKey: "tourGuide.gotIt",
      runAfter: () => {
        document.getElementById("chat-composer-input")?.focus();
      }
    }
  ];
}

function resolveVariantOnlyOldSteps(
  navigate: (path: string) => void,
  conversationHistoryId: string
): TourStepDefinition[] {
  const listCubit = Blac.getBloc(ConversationListCubit);

  return [
    {
      anchorName: ChatTourAnchor.DRAWER_TOGGLE,
      textKey: "tourGuide.chat.findPrevious",
      placement: "bottom"
    },
    {
      anchorName: ChatTourAnchor.HISTORY_ITEM,
      textKey: "tourGuide.chat.openOldThread",
      placement: "bottom",
      runBefore: () => listCubit.openDrawer(),
      runAfter: () => {
        listCubit.closeDrawer();
        navigate(chatConversationPath(conversationHistoryId));
      }
    },
    {
      anchorName: ChatTourAnchor.NEW_CONVERSATION,
      textKey: "tourGuide.chat.startNewConversation",
      placement: "bottom",
      runAfter: () => navigate(CHAT_NEW_PATH)
    },
    {
      anchorName: ChatTourAnchor.COMPOSER,
      textKey: "tourGuide.chat.askSophie",
      placement: "top",
      buttonTextKey: "tourGuide.gotIt",
      runAfter: () => {
        document.getElementById("chat-composer-input")?.focus();
      }
    }
  ];
}

const createChatTour = (config: ChatTourConfig): TourDefinition => ({
  id: CHAT_TOUR_ID,

  resolveSteps(): TourStepDefinition[] {
    const { variant, navigate, conversationHistoryId } = config;

    switch (variant) {
      case ChatTourVariant.OldAndNewFromIssue:
        return resolveVariantOldAndNewFromIssueMessagesSteps(
          navigate,
          conversationHistoryId
        );
      case ChatTourVariant.OnlyOldMessages:
        return resolveVariantOnlyOldSteps(navigate, conversationHistoryId);
      default:
        reportErrorSentry(new Error(`Unknown chat tour variant`));
        return [];
    }
  }
});

export default createChatTour;
